import { createContext, forwardRef, useCallback, useContext, useState } from "react";
import type { ChangeEvent, FocusEvent, ReactNode } from "react";
import {
  displayedNumber,
  isAcceptedNumberInput,
  toAsciiNumber,
  type NumberDigits,
} from "./numberInputValue";

/**
 * The digits every controlled NumberInput below shows a value in when it was
 * not typed into the field (a saved price, a prefill, a reset). An Arabic
 * portal provides "arab" so a saved "680" reads "٦٨٠" beside the "٦٨٠ ج.م"
 * preview; the consumer still reads ASCII. Default "latn" (unchanged).
 */
const NumberDigitsContext = createContext<NumberDigits>("latn");

export function NumberDigitsProvider({
  digits,
  children,
}: {
  digits: NumberDigits;
  children: ReactNode;
}) {
  return (
    <NumberDigitsContext.Provider value={digits}>{children}</NumberDigitsContext.Provider>
  );
}

type NumberInputProps = {
  id?: string;
  name: string;
  defaultValue?: string | number;
  value?: string | number;
  placeholder?: string;
  required?: boolean;
  hasError?: boolean;
  min?: number;
  max?: number;
  step?: number;
  /** Allow decimal numbers (default: false — integers only) */
  allowDecimal?: boolean;
  autoComplete?: string;
  dir?: "ltr" | "rtl" | "auto";
  className?: string;
  disabled?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /** Forwarded to the input — see the note on `TextInput`. */
  "data-testid"?: string;
  /**
   * The input's name for a reader when no visible `<label>` names it (a field
   * in a table row). Hyphenated JSX attributes are not type-checked, so before
   * this was declared an `aria-label` passed here was silently dropped.
   */
  "aria-label"?: string;
  "aria-describedby"?: string;
  /** Overrides `NumberDigitsProvider` for this field. */
  digits?: NumberDigits;
};

/**
 * A number input that uses `type="text"` with `inputMode="numeric"` or `"decimal"`
 * to avoid the browser's native number input scroll-to-change-value behavior.
 *
 * - Filters out non-numeric characters on input
 * - Accepts Arabic-Indic (٠–٩) and Persian (۰–۹) digits and the Arabic decimal
 *   separator "٫": the consumer's `onChange` reads ASCII from `event.target.value`
 *   while a controlled field keeps showing what was typed. An uncontrolled field
 *   (`defaultValue`, `register()`) is read from the DOM by its form, so there
 *   the typed digits are rewritten to ASCII in place.
 * - Allows a single decimal point when `allowDecimal` is true
 * - Allows a leading minus sign for negative numbers
 * - A controlled value it did not see typed (a saved price, a prefill) is shown
 *   in the digits of `NumberDigitsProvider` / `digits` — "٦٨٠" on an Arabic page
 * - Shows the numeric keyboard on mobile via `inputMode`
 * - Exposes the same API as TextInput for easy migration
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput(
    {
      id,
      name,
      defaultValue,
      value,
      placeholder,
      required,
      hasError,
      min,
      max,
      step,
      allowDecimal = false,
      autoComplete,
      dir,
      className,
      disabled,
      onChange,
      onBlur,
      "data-testid": testId,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedBy,
      digits: digitsProp,
    },
    ref
  ) {
    const contextDigits = useContext(NumberDigitsContext);
    const digits = digitsProp ?? contextDigits;
    const isControlled = value !== undefined;
    // The text as typed (any digit script) — shown while it still means the
    // consumer's value; see `displayedNumber`.
    const [typed, setTyped] = useState<string | null>(null);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        // Only digits, an optional leading minus and (when allowed) a single
        // decimal point — in ASCII, Arabic-Indic or Persian digits. Empty, "-"
        // and a bare "." / "-." are a number in progress.
        if (!isAcceptedNumberInput(raw, allowDecimal)) {
          // Reject the input — don't call onChange, don't update the value
          e.preventDefault();
          return;
        }

        const ascii = toAsciiNumber(raw);
        if (ascii === raw) {
          // Keep showing the Latin digits she typed on a page that otherwise
          // shows Arabic-Indic ones — rewriting under the caret would jump it.
          setTyped(isControlled && digits !== "latn" ? raw : null);
          onChange?.(e);
          return;
        }
        if (!isControlled) {
          // The form reads this field from the DOM: store ASCII there.
          const caret = e.target.selectionStart;
          e.target.value = ascii;
          if (caret !== null) e.target.setSelectionRange(caret, caret);
          onChange?.(e);
          return;
        }
        setTyped(raw);
        onChange?.(withValue(e, ascii));
      },
      [allowDecimal, digits, isControlled, onChange]
    );

    const stringValue =
      value !== undefined && value !== null ? String(value) : undefined;
    const stringDefault =
      defaultValue !== undefined && defaultValue !== null
        ? String(defaultValue)
        : undefined;

    return (
      <input
        ref={ref}
        id={id}
        name={name}
        data-ui="input"
        type="text"
        inputMode={allowDecimal ? "decimal" : "numeric"}
        defaultValue={isControlled ? undefined : stringDefault}
        value={
          stringValue === undefined ? undefined : displayedNumber(typed, stringValue, digits)
        }
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        autoComplete={autoComplete ?? "off"}
        dir={dir}
        aria-invalid={hasError ? "true" : undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        data-testid={testId}
        onChange={handleChange}
        onBlur={onBlur}
        className={`h-[35px] w-full rounded-md border bg-[color:var(--surface)] px-3 text-[13px] focus:outline-none ${
          hasError
            ? "border-[color:var(--error-500)] focus:border-[color:var(--error-500)]"
            : "border-[color:var(--border-light)] focus:border-[color:var(--focus-ring)]"
        } ${disabled ? "cursor-not-allowed opacity-60" : ""} ${className ?? ""}`}
      />
    );
  }
);

/**
 * The same change event, reading `ascii` as the input's value. The DOM keeps
 * the typed text (a controlled field re-renders it anyway); everything else on
 * the element — name, type, checked, validity — is the real input's.
 */
function withValue(
  e: ChangeEvent<HTMLInputElement>,
  ascii: string,
): ChangeEvent<HTMLInputElement> {
  const input = e.target;
  const target = new Proxy(input, {
    get(element, property) {
      if (property === "value") return ascii;
      const member = Reflect.get(element, property, element);
      return typeof member === "function" ? member.bind(element) : member;
    },
  });
  return Object.create(e, {
    target: { value: target },
    currentTarget: { value: target },
  }) as ChangeEvent<HTMLInputElement>;
}
